import { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useVenueId } from '../../hooks/useUserData';
import { fetchDashboardStatistics } from '../../utils/statisticsService';
import AdminPageHeader from '../shared/AdminPageHeader';
import AdminMetricStrip from '../shared/AdminMetricStrip';
import AdminFilterBar from '../shared/AdminFilterBar';
import AdminSectionCard from '../shared/AdminSectionCard';
import AdminNotice from '../shared/AdminNotice';
import AdminEmptyState from '../shared/AdminEmptyState';
import '../../styles/route_pages/Statistics.css';

const RANGE_OPTIONS = [
  { value: 'today', label: 'Bugün' },
  { value: '7d', label: '7 Gün' },
  { value: '30d', label: '30 Gün' },
];

const REQUEST_COLORS = ['#2563eb', '#14b8a6', '#94a3b8'];
const CATEGORY_COLOR = '#2563eb';
const DEFAULT_REQUEST_BREAKDOWN = [
  { type: 'pay_bill', count: 0 },
  { type: 'need_assistance', count: 0 },
];

const currencyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 2,
});

const numberFormatter = new Intl.NumberFormat('tr-TR');

const formatCurrency = (value) => currencyFormatter.format(value || 0);
const formatInteger = (value) => numberFormatter.format(value || 0);

const formatBucketLabel = (bucket) => {
  if (!bucket) return '';
  if (bucket.includes(':')) {
    return bucket.slice(11, 16);
  }

  const date = new Date(`${bucket}T00:00:00+03:00`);
  return new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: 'short' }).format(date);
};

const RankedList = ({ title, subtitle, rows, valueFormatter, emptyMessage }) => (
  <AdminSectionCard title={title} subtitle={subtitle} className="statistics-section-card">
    {rows.length === 0 ? (
      <AdminEmptyState
        title={emptyMessage}
        description="Bu alan seçili dönem içinde veri oluştukça otomatik olarak dolacaktır."
      />
    ) : (
      <div className="statistics-ranked-list">
        {rows.map((row, index) => (
          <div key={`${row.itemId}-${row.itemName}`} className="statistics-ranked-row">
            <div className="statistics-ranked-rank">{index + 1}</div>
            <div className="statistics-ranked-main">
              <div className="statistics-ranked-title">{row.itemName}</div>
            </div>
            <div className="statistics-ranked-value">{valueFormatter(row)}</div>
          </div>
        ))}
      </div>
    )}
  </AdminSectionCard>
);

const HealthCard = ({ health }) => {
  const rows = [
    { label: 'Görselsiz Ürünler', value: health.itemsWithoutImages },
    { label: 'Pasif Ürünler', value: health.passiveItems },
    { label: 'Boş Kategoriler', value: health.emptyCategories },
    { label: 'QR Kapalı Masalar', value: health.qrDisabledTables },
    { label: 'Hiç Satılmayan Ürünler', value: health.neverOrderedItems },
  ];

  return (
    <AdminSectionCard
      title="Yönetim Uyarıları"
      subtitle="Temizlik ve yapılandırma açısından takip edilmesi gereken alanlar."
      className="statistics-section-card"
    >
      <div className="statistics-health-list">
        {rows.map((row) => (
          <div key={row.label} className="statistics-health-row">
            <span>{row.label}</span>
            <strong>{formatInteger(row.value)}</strong>
          </div>
        ))}
      </div>
    </AdminSectionCard>
  );
};

const Statistics = () => {
  const venueId = useVenueId();
  const { getAccessTokenSilently } = useAuth0();
  const [range, setRange] = useState('today');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let isActive = true;

    if (!venueId) {
      setDashboard(null);
      setError('İşletme bağlamı bulunamadı.');
      setLoading(false);
      return undefined;
    }

    const loadDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const nextDashboard = await fetchDashboardStatistics(venueId, range, getAccessTokenSilently);
        if (!isActive) return;
        setDashboard(nextDashboard);
      } catch (loadError) {
        if (!isActive) return;
        setDashboard(null);
        setError(loadError.message || 'İstatistikler yüklenemedi.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      isActive = false;
    };
  }, [venueId, getAccessTokenSilently, range, retryNonce]);

  const requestBreakdown = dashboard?.requestBreakdown ?? DEFAULT_REQUEST_BREAKDOWN;
  const categoryBreakdown = dashboard?.categoryBreakdown || [];
  const trendData = dashboard?.revenueTrend || [];
  const health = dashboard?.health || {
    itemsWithoutImages: 0,
    passiveItems: 0,
    emptyCategories: 0,
    qrDisabledTables: 0,
    neverOrderedItems: 0,
  };

  const requestPieData = useMemo(
    () => requestBreakdown.map((row) => ({
      name: row.type === 'pay_bill' ? 'Hesap İste' : row.type === 'need_assistance' ? 'Yardım İsteği' : row.type,
      value: row.count,
    })),
    [requestBreakdown],
  );

  const metricItems = useMemo(
    () => [
      {
        label: 'Toplam Ciro',
        value: formatCurrency(dashboard?.kpis?.paidRevenue),
        helper: 'Seçili dönemde ödenen siparişler',
      },
      {
        label: 'Sipariş',
        value: formatInteger(dashboard?.kpis?.paidOrderCount),
        helper: 'Ödenmiş sipariş adedi',
      },
      {
        label: 'Ortalama Adisyon',
        value: formatCurrency(dashboard?.kpis?.averageOrderValue),
        helper: 'Ödenmiş sipariş ortalaması',
      },
      {
        label: 'Aktif Masa',
        value: formatInteger(dashboard?.kpis?.activeTablesNow),
        helper: 'Şu anda açık hesap taşıyan masalar',
      },
      {
        label: 'Bekleyen İstek',
        value: formatInteger(dashboard?.kpis?.pendingRequestsNow),
        helper: 'Şu anda açık servis talepleri',
      },
    ],
    [dashboard],
  );

  const hasTrendActivity = trendData.some((bucket) => bucket.orderCount > 0 || bucket.revenue > 0);
  const hasCategoryActivity = categoryBreakdown.length > 0;
  const hasRequestActivity = requestPieData.some((row) => row.value > 0);

  return (
    <div className="page-container statistics-page">
      <AdminPageHeader
        title="İstatistikler"
        subtitle="Performans, servis yükü ve yönetim sağlığı için tek bakışta özet."
        controls={(
          <AdminFilterBar
            options={RANGE_OPTIONS}
            value={range}
            onChange={setRange}
            ariaLabel="Tarih aralığı"
            className="statistics-filter-bar"
          />
        )}
      />

      {error ? (
        <AdminNotice
          tone="error"
          title="İstatistikler yüklenemedi"
          message={error}
        />
      ) : null}

      {loading ? (
        <div data-testid="statistics-loading">
          <AdminSectionCard
            title="İstatistikler yükleniyor"
            subtitle="Seçili dönem verileri hazırlanıyor."
            className="statistics-state-card"
          >
            <div className="statistics-state-body">Dashboard verileri hazırlanıyor. Bu işlem yalnızca birkaç saniye sürer.</div>
          </AdminSectionCard>
        </div>
      ) : error ? (
        <AdminSectionCard
          title="Veri getirilemedi"
          subtitle="İşletme istatistikleri şu anda okunamıyor."
          className="statistics-state-card statistics-state-card-error"
        >
          <div className="statistics-state-body">
            <button type="button" className="btn btn-secondary" onClick={() => setRetryNonce((value) => value + 1)}>
              Tekrar Dene
            </button>
          </div>
        </AdminSectionCard>
      ) : (
        <>
          <AdminMetricStrip items={metricItems} />

          <div className="statistics-layout-grid">
            <AdminSectionCard
              title="Ciro ve Sipariş Trendi"
              subtitle="Seçili dönem boyunca ödenen sipariş akışı."
              className="statistics-section-card statistics-section-card-span-two"
            >
              {hasTrendActivity ? (
                <div className="statistics-chart-wrap">
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="bucket" tickFormatter={formatBucketLabel} stroke="#64748b" />
                      <YAxis yAxisId="revenue" stroke="#2563eb" tickFormatter={(value) => `${formatInteger(value)} ₺`} />
                      <YAxis yAxisId="orders" orientation="right" stroke="#0f172a" tickFormatter={formatInteger} allowDecimals={false} />
                      <Tooltip
                        formatter={(value, name) => (name === 'Ciro' ? formatCurrency(Number(value)) : formatInteger(Number(value)))}
                        labelFormatter={formatBucketLabel}
                      />
                      <Legend />
                      <Bar yAxisId="revenue" dataKey="revenue" name="Ciro" fill="#2563eb" radius={[8, 8, 0, 0]} />
                      <Line yAxisId="orders" type="monotone" dataKey="orderCount" name="Sipariş" stroke="#0f172a" strokeWidth={3} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <AdminEmptyState
                  title="Seçili dönemde ödenmiş sipariş yok."
                  description="Trend grafiği ödenmiş sipariş verisi oluştuğunda otomatik olarak burada gösterilir."
                />
              )}
            </AdminSectionCard>

            <RankedList
              title="En Çok Satılanlar"
              subtitle="Adet bazlı ilk 10 ürün"
              rows={dashboard?.topItemsByQuantity || []}
              valueFormatter={(row) => `${formatInteger(row.quantity)} adet`}
              emptyMessage="Seçili dönemde satış verisi yok."
            />

            <RankedList
              title="En Çok Ciro Yapanlar"
              subtitle="Gelir bazlı ilk 10 ürün"
              rows={dashboard?.topItemsByRevenue || []}
              valueFormatter={(row) => formatCurrency(row.revenue)}
              emptyMessage="Seçili dönemde gelir verisi yok."
            />

            <AdminSectionCard
              title="Kategori Dağılımı"
              subtitle="Ürün kategorilerine göre miktar ve ciro dağılımı."
              className="statistics-section-card"
            >
              {hasCategoryActivity ? (
                <>
                  <div className="statistics-chart-wrap statistics-chart-wrap-compact">
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={categoryBreakdown}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="categoryName" stroke="#64748b" />
                        <YAxis stroke="#2563eb" tickFormatter={formatInteger} />
                        <Tooltip formatter={(value, name) => (name === 'Ciro' ? formatCurrency(Number(value)) : formatInteger(Number(value)))} />
                        <Legend />
                        <Bar dataKey="quantity" name="Adet" fill="#94a3b8" radius={[8, 8, 0, 0]} />
                        <Bar dataKey="revenue" name="Ciro" fill={CATEGORY_COLOR} radius={[8, 8, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="statistics-category-list">
                    {categoryBreakdown.map((row) => (
                      <div key={row.categoryId} className="statistics-category-row">
                        <div>
                          <strong>{row.categoryName}</strong>
                          <span>{formatInteger(row.quantity)} adet</span>
                        </div>
                        <strong>{formatCurrency(row.revenue)}</strong>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <AdminEmptyState
                  title="Seçili dönemde kategori bazlı satış yok."
                  description="Kategori dağılımı, dönem içinde en az bir ödenmiş sipariş oluştuğunda gösterilir."
                />
              )}
            </AdminSectionCard>

            <AdminSectionCard
              title="Servis Talepleri"
              subtitle="Seçili dönemde oluşturulan isteklerin dağılımı."
              className="statistics-section-card"
            >
              {hasRequestActivity ? (
                <div className="statistics-request-grid">
                  <div className="statistics-chart-wrap statistics-chart-wrap-compact">
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={requestPieData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={80} paddingAngle={4}>
                          {requestPieData.map((row, index) => (
                            <Cell key={row.name} fill={REQUEST_COLORS[index % REQUEST_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatInteger(Number(value))} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="statistics-request-list">
                    {requestPieData.map((row, index) => (
                      <div key={row.name} className="statistics-request-row">
                        <div className="statistics-request-label">
                          <span className="statistics-request-dot" style={{ backgroundColor: REQUEST_COLORS[index % REQUEST_COLORS.length] }} />
                          <span>{row.name}</span>
                        </div>
                        <strong>{formatInteger(row.value)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <AdminEmptyState
                  title="Seçili dönemde servis talebi yok."
                  description="Talepler oluşmaya başladığında dağılım burada görünecektir."
                />
              )}
            </AdminSectionCard>

            <HealthCard health={health} />
          </div>
        </>
      )}
    </div>
  );
};

export default Statistics;
